/*
  Maison Bagages : carte à gratter (bloc mb-carte-a-gratter). Documenté dans assets/CUSTOM.txt.
  Couche à gratter peinte dans un canvas avec les couleurs et la police du thème (lues sur un élément sonde
  qui porte la classe du schéma choisi), effacée au pointeur (souris, doigt, stylet) en mode destination-out.
  Au seuil réglé (part de surface grattée), la couche disparaît, le cadeau est annoncé (aria-live) et le
  formulaire apparaît. Inscription : API client Klaviyo (POST /client/subscriptions), comme mb-newsletter.
*/
const SUBSCRIBED_KEY = 'mb:inscrit';
const KLAVIYO_REVISION = '2025-07-15';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const store = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* stockage indisponible : sans conséquence sur l'inscription */
    }
  },
};

class MbCarteAGratter extends HTMLElement {
  #ctx = null;
  #brush = 24;
  #drawing = false;
  #last = null;
  #started = false;
  #revealed = false;
  #lastCheck = 0;
  #resizeObserver = null;

  connectedCallback() {
    this.key = `mb-carte:${this.dataset.key}`;
    this.design = Boolean(window.Shopify?.designMode);
    this.ticket = this.querySelector('[data-ref="ticket"]');
    this.canvas = this.querySelector('[data-ref="cover"]');
    this.probe = this.querySelector('[data-ref="probe"]');
    this.prize = this.querySelector('[data-ref="prize"]');
    this.live = this.querySelector('[data-ref="live"]');
    this.form = this.querySelector('[data-ref="form"]');
    this.input = this.form?.querySelector('input[type="email"]');
    this.submitButton = this.querySelector('[data-ref="submit"]');
    this.error = this.querySelector('[data-ref="error"]');
    this.success = this.querySelector('[data-ref="success"]');
    if (!this.canvas || !this.form) return;

    this.querySelector('[data-ref="reveal-button"]')?.addEventListener('click', () => this.reveal());
    this.form.addEventListener('submit', (event) => this.#submit(event));
    this.input?.addEventListener('input', () => {
      if (this.error) this.error.hidden = true;
    });

    if (!this.design && (store.get(`${this.key}:done`) === '1' || store.get(SUBSCRIBED_KEY) === '1')) {
      this.reveal({ instant: true, silent: true });
      this.#showSuccess(false);
      return;
    }

    if (!this.design && store.get(`${this.key}:revealed`) === '1') {
      this.reveal({ instant: true, silent: true });
      return;
    }

    this.#bindScratch();
    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.#started && !this.#revealed) this.#paint();
    });
    this.#resizeObserver.observe(this.ticket);
    document.fonts?.ready.then(() => {
      if (!this.#started && !this.#revealed) this.#paint();
    });
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  reveal({ instant = false, silent = false } = {}) {
    if (this.#revealed) return;
    this.#revealed = true;
    this.#drawing = false;
    this.classList.add('is-revealed');
    if (instant) this.classList.add('is-instant');
    this.prize?.removeAttribute('aria-hidden');
    this.canvas.setAttribute('aria-hidden', 'true');
    this.form.hidden = false;
    if (!silent && this.live) this.live.textContent = this.dataset.announce || '';
    if (!this.design) store.set(`${this.key}:revealed`, '1');
    this.dispatchEvent(new CustomEvent('mb-carte:revealed', { bubbles: true }));

    if (!silent && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.input?.focus({ preventScroll: true });
    }
  }

  #paint() {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(rect.width * ratio);
    this.canvas.height = Math.round(rect.height * ratio);
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    this.#ctx = ctx;

    const style = getComputedStyle(this.probe);
    const width = rect.width;
    const height = rect.height;
    this.#brush = Math.max(18, Math.min(width, height) * 0.13);

    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = style.backgroundColor;
    ctx.fillRect(0, 0, width, height);

    // Hachures légères et liseré pointillé : effet ticket, couleur du texte du schéma à faible opacité.
    ctx.save();
    ctx.strokeStyle = style.color;
    ctx.globalAlpha = 0.07;
    ctx.lineWidth = 1;
    for (let x = -height; x < width; x += 9) {
      ctx.beginPath();
      ctx.moveTo(x, height);
      ctx.lineTo(x + height, 0);
      ctx.stroke();
    }
    ctx.globalAlpha = 0.35;
    ctx.setLineDash([5, 5]);
    const inset = Math.min(10, height * 0.08);
    ctx.strokeRect(inset, inset, width - inset * 2, height - inset * 2);
    ctx.restore();

    const label = this.dataset.coverLabel || '';
    if (label) {
      const size = Math.round(Math.max(18, Math.min(30, height * 0.17)));
      ctx.fillStyle = style.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `${style.fontWeight} ${size}px ${style.fontFamily}`;
      ctx.fillText(label, width / 2, height / 2, width - inset * 4);
    }
  }

  #bindScratch() {
    const point = (event) => {
      const rect = this.canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    this.canvas.addEventListener('pointerdown', (event) => {
      if (this.#revealed || !this.#ctx) return;
      event.preventDefault();
      this.canvas.setPointerCapture?.(event.pointerId);
      this.#drawing = true;
      this.#started = true;
      this.#last = point(event);
      this.#scratch(this.#last, this.#last);
    });

    this.canvas.addEventListener('pointermove', (event) => {
      if (!this.#drawing) return;
      event.preventDefault();
      const current = point(event);
      this.#scratch(this.#last, current);
      this.#last = current;
      this.#checkProgress(false);
    });

    const stop = () => {
      if (!this.#drawing) return;
      this.#drawing = false;
      this.#checkProgress(true);
    };
    this.canvas.addEventListener('pointerup', stop);
    this.canvas.addEventListener('pointercancel', stop);
    this.canvas.addEventListener('lostpointercapture', stop);
  }

  #scratch(from, to) {
    const ctx = this.#ctx;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = this.#brush * 2;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x + 0.01, to.y);
    ctx.stroke();
  }

  #checkProgress(force) {
    const now = performance.now();
    if (!force && now - this.#lastCheck < 120) return;
    this.#lastCheck = now;

    const { width, height } = this.canvas;
    if (!width || !height) return;
    const data = this.#ctx.getImageData(0, 0, width, height).data;
    const columns = 32;
    const rows = 16;
    let cleared = 0;
    for (let row = 0; row < rows; row++) {
      const y = Math.floor(((row + 0.5) / rows) * height);
      for (let column = 0; column < columns; column++) {
        const x = Math.floor(((column + 0.5) / columns) * width);
        if (data[(y * width + x) * 4 + 3] < 128) cleared++;
      }
    }

    if ((cleared / (columns * rows)) * 100 >= (Number(this.dataset.threshold) || 45)) this.reveal();
  }

  async #submit(event) {
    event.preventDefault();
    const email = this.input?.value.trim() || '';
    if (!EMAIL_PATTERN.test(email)) {
      this.#showError();
      return;
    }

    this.submitButton?.classList.add('is-loading');
    this.submitButton?.setAttribute('aria-busy', 'true');
    const ok = await this.#subscribe(email);
    this.submitButton?.classList.remove('is-loading');
    this.submitButton?.removeAttribute('aria-busy');

    if (!ok) {
      this.#showError();
      return;
    }

    try {
      if (window.klaviyo?.identify) window.klaviyo.identify({ email });
    } catch {
      /* suivi Klaviyo indisponible : l'inscription est déjà faite */
    }

    if (!this.design) {
      store.set(`${this.key}:done`, '1');
      store.set(SUBSCRIBED_KEY, '1');
    }
    this.#showSuccess(true);
    this.dispatchEvent(new CustomEvent('mb-popup:done', { bubbles: true }));
  }

  #subscribe(email) {
    const { company, list, source, gift } = this.dataset;
    if (!company || !list) return Promise.resolve(false);

    const properties = { source_inscription: source || 'Popup' };
    if (gift) properties.cadeau_bienvenue = gift;

    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source || 'Popup',
          profile: { data: { type: 'profile', attributes: { email, properties } } },
        },
        relationships: { list: { data: { type: 'list', id: list } } },
      },
    };

    return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body),
    })
      .then((response) => response.ok)
      .catch(() => false);
  }

  #showError() {
    if (this.error) this.error.hidden = false;
    this.input?.focus();
  }

  #showSuccess(focus) {
    this.form.hidden = true;
    if (!this.success) return;
    this.success.hidden = false;
    if (focus) this.success.focus({ preventScroll: true });
  }
}

if (!customElements.get('mb-carte-a-gratter')) {
  customElements.define('mb-carte-a-gratter', MbCarteAGratter);
}
